// R4 renderer-only contacts; generated R2 PNG/calibration remain immutable.
import {worldToScreen} from './coordinates';
import type {WallSection} from './fortification-geometry';
import {CAPITAL_ROYAL_TOWER_ART_PROFILE as art} from './capital-royal-art';
type Point={x:number;y:number};
export interface TowerWallContactProfile {shape?:'square'|'cylinder';contactHalfWidth:number;opaqueMinX:number;opaqueMaxX:number;wallFaceOffset:number;sections:readonly {height:number;halfWidth:number}[]}
export const CAPITAL_ROYAL_JOINT_PROFILE={
 contactHalfWidth:1.8+.25,opaqueMinX:95,opaqueMaxX:1126,
 wallFaceOffset:.25,
 // Actual side silhouette transitions in the final1221x1315 PNG. The
 // left/right sides are averaged; heights use the calibrated ground centre.
 // A conservative crown envelope protects the source cap/merlons.
 sections:[
  {height:0,halfWidth:1.8},
  {height:(art.ground.y-820)*art.displayHeight/art.bodyHeight,halfWidth:((610-192)+(1030-610))/2*art.displayHeight/art.bodyHeight/96},
  {height:(art.ground.y-620)*art.displayHeight/art.bodyHeight,halfWidth:((610-195)+(1027-610))/2*art.displayHeight/art.bodyHeight/96},
  {height:(art.ground.y-500)*art.displayHeight/art.bodyHeight,halfWidth:((610-155)+(1067-610))/2*art.displayHeight/art.bodyHeight/96},
  {height:330,halfWidth:1.8},
 ],
} as const;
function hull(points:Point[]):Point[]{
 const sorted=points.slice().sort((a,b)=>a.x-b.x||a.y-b.y);
 const cross=(a:Point,b:Point,c:Point)=>(b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
 const half=(list:Point[])=>{const out:Point[]=[];for(const p of list){while(out.length>1&&cross(out[out.length-2]!,out[out.length-1]!,p)<=1e-8)out.pop();out.push(p)}return out};
 const a=half(sorted),b=half(sorted.slice().reverse());return a.slice(0,-1).concat(b.slice(0,-1));
}
/** Exact halfspace clipping of a circumscribed24-sided conical slab. Keeping
 * clipped cap edges AND vertical/sloping edges preserves the real cylindrical
 * contact as its radius changes; square Capital dimensions are never reused. */
function solidFrontSlab(a:{height:number;halfWidth:number},b:{height:number;halfWidth:number},offset:number,alongX:boolean,tower:Point,shape:'square'|'cylinder'):Point[]{
 const count=24,inflation=1/Math.cos(Math.PI/count);
 type Vertex={u:number;v:number;height:number};
 const ring=(q:typeof a):Vertex[]=>shape==='square'?[{u:-q.halfWidth,v:-q.halfWidth,height:q.height},{u:q.halfWidth,v:-q.halfWidth,height:q.height},{u:q.halfWidth,v:q.halfWidth,height:q.height},{u:-q.halfWidth,v:q.halfWidth,height:q.height}]:Array.from({length:count},(_,i)=>{const angle=(2*i+1)*Math.PI/count;return{u:q.halfWidth*inflation*Math.cos(angle),v:q.halfWidth*inflation*Math.sin(angle),height:q.height};});
 const lower=ring(a),upper=ring(b),points:Vertex[]=[...lower,...upper].filter(p=>p.v>=offset);
 const clipEdge=(p:Vertex,q:Vertex)=>{if((p.v-offset)*(q.v-offset)<0){const f=(offset-p.v)/(q.v-p.v);points.push({u:p.u+(q.u-p.u)*f,v:offset,height:p.height+(q.height-p.height)*f});}};
 for(let i=0;i<lower.length;i++){clipEdge(lower[i]!,lower[(i+1)%lower.length]!);clipEdge(upper[i]!,upper[(i+1)%upper.length]!);clipEdge(lower[i]!,upper[i]!);}
 return points.length>2?hull(points.map(q=>{const p=worldToScreen(tower.x+(alongX?q.u:q.v),tower.y+(alongX?q.v:q.u));return{x:p.x,y:p.y-q.height};})):[];
}
/** Project only the part of the solid tower IN FRONT of the painted wall face.
 * The actual curtain face is +.25WU normal from its saved centreline. Taking
 * the union of these frustum projections is a ray/volume occlusion test: the
 * foreground copy is cut there, while its unchanged twin stays behind tower.
 * Unlike a screen rectangle, this keeps the flare in front of the lower wall
 * and lets the upper curtain meet the narrower shaft on the correct face.
 */
export function royalTowerFrontVolumes(section:WallSection,towers:readonly Point[],profile:TowerWallContactProfile=CAPITAL_ROYAL_JOINT_PROFILE):Point[][]{
 const alongX=Math.abs(section.end.x-section.start.x)>=Math.abs(section.end.y-section.start.y);
 const normal=(alongX?section.start.y:section.start.x)+profile.wallFaceOffset;
 const polygons:Point[][]=[];
 const ends=[worldToScreen(section.start.x,section.start.y),worldToScreen(section.end.x,section.end.y)];
 const minX=Math.min(...ends.map(p=>p.x))-32,maxX=Math.max(...ends.map(p=>p.x))+32;
 const minY=Math.min(...ends.map(p=>p.y))-512,maxY=Math.max(...ends.map(p=>p.y))+128;
 for(const t of towers){
  const offset=normal-(alongX?t.y:t.x),levels=profile.sections;
  // A negative-axis arm approaches the hidden rear of the shaft. Its complete
  // pass stays behind; only the positive-axis half can expose a front contact.
  // At the painted face, x_world=tower.x projects12px left of the centre for
  // an X curtain, and y_world=tower.y projects12px right for a Y curtain.
  const centre=worldToScreen(t.x,t.y).x,cut=centre+(alongX?-offset:offset)*48;
  const low=alongX?minX:Math.max(minX,cut),high=alongX?Math.min(maxX,cut):maxX;
  if(high>low)polygons.push([{x:low,y:minY},{x:high,y:minY},{x:high,y:maxY},{x:low,y:maxY}]);
  for(let i=1;i<levels.length;i++){
   const a=levels[i-1]!,b=levels[i]!,points:Point[]=[];
   if(profile.shape){const polygon=solidFrontSlab(a,b,offset,alongX,t,profile.shape);if(polygon.length>2)polygons.push(polygon);continue;}
   for(const q of[a,b]){
    if(q.halfWidth<offset)continue;
    for(const u of[-q.halfWidth,q.halfWidth])for(const v of[Math.max(-q.halfWidth,offset),q.halfWidth]){
     const p=worldToScreen(t.x+(alongX?u:v),t.y+(alongX?v:u));points.push({x:p.x,y:p.y-q.height});
    }
   }
   // If a narrow offset intersects only part of a tapered slab, add its exact
   // plane intersection rather than extrapolating the visible foundation.
   if((a.halfWidth-offset)*(b.halfWidth-offset)<0){
    const f=(offset-a.halfWidth)/(b.halfWidth-a.halfWidth),height=a.height+(b.height-a.height)*f;
    for(const u of[-offset,offset]){const p=worldToScreen(t.x+(alongX?u:offset),t.y+(alongX?offset:u));points.push({x:p.x,y:p.y-height})}
   }
   if(points.length>2)polygons.push(hull(points));
  }
 }
 return polygons;
}
